/* eslint-disable prettier/prettier */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Container,
  Card,
  Row,
  Col,
  Badge,
  Button,
  Form,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from '../../services/axios';
import Breadcrumbs from '../../components/Breadcrumbs';
import Loading from '../../components/Loading';
import ErrorProcessor from '../../components/ErrorProcessor';

export default function InventoryIndex() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [apiErrors, setApiErrors] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [tagId, setTagId] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');

  const parseAssetsFromNotes = useMemo(
    () => (notes) => {
      if (!notes || typeof notes !== 'string') return [];
      const start = notes.indexOf('[[INVENTORY]]');
      const end = notes.indexOf('[[/INVENTORY]]');
      if (start === -1 || end === -1 || end <= start) return [];
      const jsonText = notes
        .substring(start + '[[INVENTORY]]'.length, end)
        .trim();
      try {
        const data = JSON.parse(jsonText);
        return Array.isArray(data) ? data : [];
      } catch (_) {
        return [];
      }
    },
    []
  );

  const loadTagId = useCallback(async () => {
    try {
      const { data } = await axios.get('/tags');
      const tag = (data || []).find((t) => t.name === 'Possui Ativos');
      setTagId(tag ? tag.id : null);
    } catch (err) {
      setTagId(null);
    }
  }, []);

  const loadCustomers = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {};
      if (tagId) params.tagId = tagId;
      if (debouncedSearchText) params.q = debouncedSearchText;
      const { data } = await axios.get('/customers', { params });
      const raw = data.customers || data || [];
      const onlyFlagged = raw.filter(
        (c) =>
          Array.isArray(c.tags) &&
          c.tags.some((t) => t.name === 'Possui Ativos')
      );
      const ids = onlyFlagged.map((c) => c.id);
      let countsMap = {};
      if (ids.length) {
        try {
          const { data: countsData } = await axios.get('/assets/counts', {
            params: { customerIds: ids.join(',') },
          });
          (countsData.counts || []).forEach((row) => {
            countsMap[row.customer_id] = row.count;
          });
        } catch (err2) {
          countsMap = {};
        }
      }
      const list = onlyFlagged.map((c) => ({
        id: c.id,
        nomeEmpresa: c.nomeEmpresa,
        razaoSocial: c.razaoSocial,
        assetsCount: countsMap[c.id] ?? 0,
      }));
      setCustomers(list);
    } catch (err) {
      setApiErrors(err);
      setCustomers([]);
    } finally {
      setIsLoading(false);
    }
  }, [tagId, debouncedSearchText, parseAssetsFromNotes]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchText(searchText.trim()), 400);
    return () => clearTimeout(t);
  }, [searchText]);

  useEffect(() => {
    loadTagId();
  }, [loadTagId]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  return (
    <Container fluid className="px-lg-4 px-xl-5">
      <Loading isLoading={isLoading} />
      <Breadcrumbs title="Inventário" />
      <ErrorProcessor error={apiErrors} />

      <Row className="mb-3">
        <Col>
          <h1 className="page-heading">Clientes com Ativos</h1>
        </Col>
        <Col xs="auto">
          <Badge bg={tagId ? 'success' : 'warning'}>
            {tagId
              ? 'Etiqueta Possui Ativos encontrada'
              : 'Crie a etiqueta "Possui Ativos"'}
          </Badge>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6} lg={5} xl={4}>
          <Form.Group controlId="inventorySearch">
            <Form.Label>Buscar cliente</Form.Label>
            <Form.Control
              type="search"
              placeholder="Pesquisar por nome fantasia ou razão social..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      <Card className="card-table">
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr>
                <th>Nome Fantasia</th>
                <th>Razão Social</th>
                <th>Qtd. Ativos</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center text-muted py-4">
                    Nenhum cliente com ativos
                  </td>
                </tr>
              ) : (
                customers.map((c) => (
                  <tr key={c.id}>
                    <td>{c.nomeEmpresa || '-'}</td>
                    <td>{c.razaoSocial || '-'}</td>
                    <td>
                      <Badge bg={c.assetsCount > 0 ? 'primary' : 'secondary'}>
                        {c.assetsCount}
                      </Badge>
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => navigate(`/inventory/${c.id}`)}
                      >
                        Gerenciar Ativos
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </Container>
  );
}
